import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, TrendingUp, Sparkles, ShieldCheck } from "lucide-react";
import { GoldButton, GhostButton } from "@/components/GoldButton";
import { CountUp } from "@/components/CountUp";
import { useWaitlist } from "@/context/WaitlistContext";

const HEADLINE_LINES = ["The Gold Wealth", "Platform for the", "Indian World"];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PhoneMockup = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -60]);

  return (
    <motion.div ref={ref} style={{ y }} className="relative">
      <motion.div
        initial={{ opacity: 0, scale: 0.92, rotateY: -12 }}
        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
        transition={{ duration: 1.1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative mx-auto w-[300px] animate-float"
        style={{ perspective: 1000 }}
      >
        {/* glow */}
        <div className="absolute -inset-8 rounded-[3rem] bg-gradient-to-tr from-gold/25 via-gold-rose/20 to-transparent blur-3xl" />

        <div className="relative rounded-[2.6rem] border border-amber-200/60 bg-ivory-light p-3 shadow-[0_30px_70px_-20px_rgba(42,36,32,0.35)]">
          <div className="overflow-hidden rounded-[2.1rem] bg-gradient-to-b from-white to-ivory">
            {/* status bar */}
            <div className="flex items-center justify-between px-6 pt-5 text-[11px] font-medium text-charcoal-soft">
              <span>9:41</span>
              <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-gold" /> Vaulted</span>
            </div>

            <div className="px-6 pb-7 pt-4">
              <p className="text-xs uppercase tracking-[0.18em] text-charcoal-soft">Your gold balance</p>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="font-serif text-4xl text-charcoal">₹</span>
                <CountUp to={247850} className="font-serif text-4xl tracking-tight text-charcoal" />
              </div>
              <div className="mt-1.5 flex items-center gap-2 text-sm">
                <span className="flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 font-semibold text-green-700">
                  <TrendingUp className="h-3.5 w-3.5" /> +2.4%
                </span>
                <span className="text-charcoal-soft">today · 38.2g</span>
              </div>

              {/* mini area chart */}
              <div className="mt-5 h-24 w-full">
                <svg viewBox="0 0 260 96" className="h-full w-full" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="heroFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <motion.path
                    d="M0 70 C 30 60, 45 40, 70 46 C 100 53, 120 20, 150 28 C 180 36, 205 14, 260 8"
                    fill="none" stroke="#D4AF37" strokeWidth="3" strokeLinecap="round"
                    initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                    transition={{ duration: 1.6, delay: 1.1, ease: "easeInOut" }}
                  />
                  <path d="M0 70 C 30 60, 45 40, 70 46 C 100 53, 120 20, 150 28 C 180 36, 205 14, 260 8 L260 96 L0 96 Z" fill="url(#heroFill)" />
                </svg>
              </div>

              {/* SIP pill */}
              <div className="mt-4 flex items-center justify-between rounded-2xl border border-amber-200/50 bg-white/70 p-3">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gold/15">
                    <Sparkles className="h-4 w-4 text-gold" />
                  </span>
                  <div>
                    <p className="text-[13px] font-semibold text-charcoal">Diwali Gold SIP</p>
                    <p className="text-[11px] text-charcoal-soft">₹2,000 / month · active</p>
                  </div>
                </div>
                <span className="h-2.5 w-2.5 rounded-full bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.2)]" />
              </div>
            </div>
          </div>
        </div>

        {/* floating badge */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          className="absolute -left-8 top-[54%] hidden rounded-2xl border border-amber-200/60 bg-white/90 px-4 py-3 shadow-lg backdrop-blur-md sm:block"
        >
          <p className="text-[10px] uppercase tracking-widest text-charcoal-soft">999.9 Fine</p>
          <p className="font-serif text-lg text-charcoal">24K Gold</p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const { openWaitlist } = useWaitlist();
  return (
    <section id="hero" data-testid="hero-section" className="hero-mesh relative overflow-hidden pb-24 pt-36 sm:pt-44">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-200/60 bg-white/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-charcoal-soft backdrop-blur-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-gold" /> श्रीधन · Auspicious Wealth
          </motion.div>

          <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-charcoal sm:text-6xl lg:text-7xl">
            {HEADLINE_LINES.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {i === 2 ? <span className="gold-shimmer">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-7 max-w-xl text-lg leading-relaxed text-charcoal-soft"
          >
            Buy, save and gift 24K digital gold — rooted in Indian tradition, powered by
            technology, borderless for the diaspora. Build a golden corpus from just ₹10.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <GoldButton data-testid="hero-start-saving-btn" size="lg" icon={ArrowRight} onClick={() => window.open("https://shreedhani.com/", "_blank", "noopener,noreferrer")}>
              Start Saving
            </GoldButton>
            <a href="#how-it-works">
              <GhostButton data-testid="hero-how-it-works-btn">See how it works</GhostButton>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 flex items-center gap-6 text-sm text-charcoal-soft"
          >
            <div className="flex -space-x-2">
              {["#D4AF37", "#E8B45C", "#E8C4A0", "#C08457"].map((c, i) => (
                <span key={i} className="h-8 w-8 rounded-full border-2 border-ivory" style={{ background: c }} />
              ))}
            </div>
            <span><span className="font-semibold text-charcoal">50,000+</span> savers building golden wealth</span>
          </motion.div>
        </div>

        {/* Right */}
        <div className="relative">
          <PhoneMockup />
        </div>
      </div>
    </section>
  );
};
